import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { listTemplates, forkTemplate, orgTemplateCount, listTeams } from '../api'

interface Template {
  name: string
  title: string
  description: string
  category: string
  fork_count: number
}

const CATEGORIES = ['All', 'Communication', 'Marketing', 'Product', 'Sales', 'Operations']

const CAT_COLORS: Record<string, { bg: string; text: string; iconBg: string }> = {
  Communication: { bg: 'rgba(124,58,237,0.1)', text: '#a78bfa', iconBg: 'rgba(124,58,237,0.15)' },
  Marketing:     { bg: 'rgba(16,185,129,0.1)', text: '#10b981', iconBg: 'rgba(16,185,129,0.15)' },
  Product:       { bg: 'rgba(59,130,246,0.1)', text: '#3b82f6', iconBg: 'rgba(59,130,246,0.15)' },
  Sales:         { bg: 'rgba(245,158,11,0.1)', text: '#f59e0b', iconBg: 'rgba(245,158,11,0.15)' },
  Operations:    { bg: 'rgba(239,68,68,0.1)',  text: '#ef4444', iconBg: 'rgba(239,68,68,0.15)' },
}

const CAT_ICONS: Record<string, React.ReactNode> = {
  Communication: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  Marketing:     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  Product:       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>,
  Sales:         <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
  Operations:    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
}

export default function TemplatesPage() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('All')
  const [forking, setForking] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [orgCount, setOrgCount] = useState(0)
  const [teamName, setTeamName] = useState('')

  useEffect(() => {
    listTemplates()
      .then(r => setTemplates(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
    if (session) {
      orgTemplateCount().then(r => setOrgCount(r.data?.count || 0)).catch(() => {})
      listTeams().then(r => {
        const t = Array.isArray(r.data) ? r.data : (r.data?.data ?? [])
        if (t.length) setTeamName(t[0].name)
      }).catch(() => {})
    }
  }, [session])

  const filtered = templates.filter(t => {
    if (category !== 'All' && t.category !== category) return false
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) && !t.description.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const handleFork = async (t: Template) => {
    if (!session) {
      navigate(`/login?redirect=/templates`)
      return
    }
    setForking(t.name)
    try {
      const r = await forkTemplate(t.name)
      navigate(`/templates/${t.name}/build`, { state: { skillName: r.data.skill_name, sections: r.data.sections, templateTitle: t.title } })
    } catch (e: any) {
      setToast(e.response?.data?.detail || 'Failed to fork template')
      setTimeout(() => setToast(''), 3000)
    } finally {
      setForking(null)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text1)' }}>
      {/* Nav */}
      <nav style={{
        height: 56, display: 'flex', alignItems: 'center', padding: '0 24px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)',
        backdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <svg width="28" height="28" viewBox="0 0 28 28"><rect width="28" height="28" rx="6" fill="#7c3aed"/><rect x="4" y="7" width="20" height="4.5" rx="2.25" fill="white" transform="rotate(-10,14,9.25)"/><rect x="4" y="14" width="14" height="4.5" rx="2.25" fill="white" opacity=".55" transform="rotate(-10,11,16.25)"/><rect x="4" y="21" width="8" height="4.5" rx="2.25" fill="white" opacity=".22" transform="rotate(-10,8,23.25)"/></svg>
          <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 17, fontWeight: 700, color: 'var(--text1)' }}>Skil<span style={{ color: 'var(--brand-light)' }}>flow</span></span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginLeft: 32 }}>
          {session && <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>Dashboard</button>}
          <button style={{ background: 'none', border: 'none', color: 'var(--text1)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Templates</button>
        </div>
        <div style={{ flex: 1 }} />
        {session ? (
          <button onClick={() => navigate('/')} style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 8,
            padding: '6px 14px', color: 'var(--text2)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}>Dashboard</button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => navigate('/login?redirect=/templates')} style={{
              background: 'none', border: 'none', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}>Sign in</button>
            <button onClick={() => navigate('/signup')} className="btn-primary" style={{ padding: '8px 16px', fontSize: 13 }}>
              Get started free
            </button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '64px 24px 40px', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 36, fontWeight: 700, letterSpacing: -0.5, marginBottom: 12 }}>
          Skill Templates
        </h1>
        <p style={{ fontSize: 16, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 32 }}>
          Start with proven templates. Fork, customize, and publish to your team in minutes.
        </p>
        <div style={{ position: 'relative', maxWidth: 480, margin: '0 auto' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search templates..."
            style={{
              width: '100%', padding: '12px 16px 12px 40px', borderRadius: 10,
              background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text1)',
              fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Tab switcher */}
      {session && orgCount > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 20, padding: '0 24px' }}>
          <button style={{
            padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', fontFamily: 'inherit',
            background: 'var(--brand)', color: '#fff', cursor: 'pointer',
          }}>Skilflow templates</button>
          <button onClick={() => navigate('/templates/org')} style={{
            padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, border: 'none', fontFamily: 'inherit',
            background: 'var(--surface2)', color: 'var(--text2)', cursor: 'pointer',
          }}>
            {teamName || 'Org'} templates
            <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: 'rgba(13,148,136,0.15)', color: '#0d9488' }}>{orgCount}</span>
          </button>
        </div>
      )}

      {/* Filter pills */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 40, flexWrap: 'wrap', padding: '0 24px' }}>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCategory(c)}
            style={{
              padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500,
              cursor: 'pointer', border: 'none', fontFamily: 'inherit', transition: 'all 0.15s',
              background: category === c ? 'var(--brand)' : 'var(--surface2)',
              color: category === c ? '#fff' : 'var(--text2)',
            }}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Card grid */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 80px' }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {[0,1,2,3,4,5].map(i => (
              <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, height: 200, animation: 'skelpulse 1.5s ease infinite', animationDelay: `${i * 0.1}s` }} />
            ))}
            <style>{`@keyframes skelpulse { 0%,100% { opacity: 0.4 } 50% { opacity: 0.8 } }`}</style>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
            <div style={{ fontSize: 14 }}>No templates match your search.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {filtered.map(t => {
              const colors = CAT_COLORS[t.category] || CAT_COLORS.Communication
              return (
                <div key={t.name} style={{
                  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
                  padding: 24, display: 'flex', flexDirection: 'column', gap: 14,
                  transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
                  cursor: 'default',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,58,237,0.4)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(124,58,237,0.08)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: colors.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text }}>
                      {CAT_ICONS[t.category]}
                    </div>
                    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text }}>
                      {t.category}
                    </span>
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 6 }}>{t.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {t.description}
                    </div>
                  </div>
                  <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t.fork_count > 0 ? (
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>Used by {t.fork_count} team{t.fork_count !== 1 ? 's' : ''}</span>
                    ) : <span />}
                    <button
                      onClick={() => handleFork(t)}
                      disabled={forking === t.name}
                      style={{
                        background: 'none', border: 'none', color: 'var(--brand-light)', fontSize: 13,
                        fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        opacity: forking === t.name ? 0.5 : 1,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      {forking === t.name ? 'Adding...' : 'Use template'} <span style={{ fontSize: 15 }}>&rarr;</span>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500, zIndex: 400,
          background: 'var(--text1)', color: 'var(--bg)', whiteSpace: 'nowrap',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}>
          {toast}
        </div>
      )}
    </div>
  )
}
